/**
 * Pestaña FICHA: qué es esta competencia y con qué reglas se calcula su tabla.
 *
 * Es la pantalla que contesta la pregunta del dirigente —"¿por qué el que
 * perdió 3-2 sumó un punto?"— sin que nadie tenga que abrir el reglamento ni
 * el código: el sistema de puntos y el orden de los desempates son datos de la
 * competencia y se muestran tal cual están guardados.
 */
import type { Competition } from '@scout/core';
import type { MessageKey } from '../../i18n/index';
import { useT } from '../../i18n/use-t';
import { Card, Empty } from '../../components/States';
import { tiebreakLabelKey } from '../../lib/competition';
import { statusLabel } from '../CompetitionsPage';

const PHASE_KIND_KEYS: Record<Competition['phases'][number]['kind'], MessageKey> = {
  regular: 'competition.phaseKindRegular',
  groups: 'competition.phaseKindGroups',
  playoff: 'competition.phaseKindPlayoff',
};

export function InfoTab({ competition }: { competition: Competition }): JSX.Element {
  const t = useT();
  const names = new Map(competition.teams.map((team) => [team.teamId, team.name]));

  return (
    <>
      <Card title={competition.name} actions={<span className="hint">{competition.id}</span>}>
        <div className="grid-2">
          <div>
            <div className="hint">{t('competition.colSeason')}</div>
            <div>{competition.season || '·'}</div>
          </div>
          <div>
            <div className="hint">{t('competition.colStatus')}</div>
            <div>{statusLabel(competition.status, t)}</div>
          </div>
        </div>
        {competition.description !== undefined ? <p>{competition.description}</p> : null}
      </Card>

      <Card title={t('competition.scoringTitle')}>
        <p className="hint">{t('competition.scoringIntro')}</p>
        {competition.scoring.label !== undefined ? <p>{competition.scoring.label}</p> : null}
        <ul>
          {competition.scoring.rules.map((rule) => (
            <li key={`${rule.winnerSets}-${rule.loserSets}`}>
              {t('competition.scoringRule', {
                winner: rule.winnerSets,
                loser: rule.loserSets,
                winnerPoints: rule.winnerPoints,
                loserPoints: rule.loserPoints,
              })}
            </li>
          ))}
          <li>
            {t('competition.scoringFallback', {
              winnerPoints: competition.scoring.winPoints,
              loserPoints: competition.scoring.lossPoints,
            })}
          </li>
          <li>
            {t('competition.scoringForfeit', {
              sets: `${competition.scoring.forfeit.winnerSets}-${competition.scoring.forfeit.loserSets}`,
              points: competition.scoring.forfeit.pointsPerSet,
            })}
          </li>
        </ul>

        <h3>{t('competition.tiebreakTitle')}</h3>
        <ol>
          {competition.tiebreakers.map((criterion) => (
            <li key={criterion}>{t(tiebreakLabelKey(criterion))}</li>
          ))}
        </ol>
      </Card>

      <Card title={t('competition.phasesTitle')}>
        {competition.phases.length === 0 ? (
          <Empty>{t('competition.fixtureEmpty')}</Empty>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>{t('competition.phase')}</th>
                  <th>{t('competition.colStatus')}</th>
                  <th>{t('competition.legs')}</th>
                  <th className="num">{t('competition.colTeams')}</th>
                  <th>{t('competition.group')}</th>
                </tr>
              </thead>
              <tbody>
                {competition.phases.map((phase) => (
                  <tr key={phase.id}>
                    <td>
                      <strong>{phase.name}</strong>
                      <div className="hint">{phase.id}</div>
                    </td>
                    <td>{t(PHASE_KIND_KEYS[phase.kind])}</td>
                    <td>{phase.legs === 2 ? t('competition.legsTwo') : t('competition.legsOne')}</td>
                    <td className="num">
                      {phase.groups.length > 0
                        ? phase.groups.reduce((n, g) => n + g.teamIds.length, 0)
                        : phase.teamIds.length}
                    </td>
                    <td>
                      {phase.groups.length === 0 ? (
                        <span className="hint">·</span>
                      ) : (
                        phase.groups.map((group) => (
                          <div key={group.id}>
                            <strong>{group.name}</strong>{' '}
                            <span className="hint">
                              {group.teamIds.map((id) => names.get(id) ?? id).join(', ')}
                            </span>
                          </div>
                        ))
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card title={t('competition.teamsTitle')}>
        {competition.teams.length === 0 ? (
          <Empty>{t('competition.linkEmpty')}</Empty>
        ) : (
          <ul>
            {competition.teams.map((team) => (
              <li key={team.teamId}>
                {team.name} <span className="hint">{team.teamId}</span>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </>
  );
}
